import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import BlockIcon from '@mui/icons-material/Block';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StarIcon from '@mui/icons-material/Star';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InsightsIcon from '@mui/icons-material/Insights';
import { KeywordItem, NegativeKeyword, MicroMarket, Typology, QueryPerformanceStatus } from '../../types/seo';
import GoogleSearchConsoleSyncModal from './GoogleSearchConsoleSyncModal';
import QueryPerformanceHistoryModal from './QueryPerformanceHistoryModal';

interface KeywordIntelligenceTabProps {
  keywords: KeywordItem[];
  negativeKeywords: NegativeKeyword[];
  onAddKeyword: (kw: KeywordItem) => void;
  onExcludeKeyword: (keywordId: string, reason: string, stem: string, isPermanent: boolean) => void;
  onRemoveNegative: (id: string) => void;
  onAddNegative: (neg: NegativeKeyword) => void;
  onMergeKeywords?: (newKeywords: KeywordItem[]) => void;
}

export default function KeywordIntelligenceTab({
  keywords,
  negativeKeywords,
  onAddKeyword,
  onExcludeKeyword,
  onRemoveNegative,
  onAddNegative,
  onMergeKeywords,
}: KeywordIntelligenceTabProps) {
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedMarket, setSelectedMarket] = useState<string>('all');
  const [selectedPossession, setSelectedPossession] = useState<string>('all');
  const [selectedIntent, setSelectedIntent] = useState<string>('all');
  const [selectedPerformance, setSelectedPerformance] = useState<string>('all');

  // Time-Series Analytics Drawer/Modal State
  const [selectedKeywordForHistory, setSelectedKeywordForHistory] = useState<KeywordItem | null>(null);

  // GSC Live Sync Modal State
  const [gscModalOpen, setGscModalOpen] = useState(false);


  // Exclusion Modal State
  const [excludeModalOpen, setExcludeModalOpen] = useState(false);
  const [targetKeywordToExclude, setTargetKeywordToExclude] = useState<KeywordItem | null>(null);
  const [exclusionStem, setExclusionStem] = useState('');
  const [exclusionReason, setExclusionReason] = useState('Non-buyer / Irrelevant traffic');
  const isPermanentExclusion = true;

  // New Keyword Modal State
  const [addKeywordModalOpen, setAddKeywordModalOpen] = useState(false);
  const [newQuery, setNewQuery] = useState('');
  const [newMonthlySearches, setNewMonthlySearches] = useState(0);
  const [newMarket, setNewMarket] = useState<MicroMarket>('Vesu');
  const [newTypology, setNewTypology] = useState<Typology>('3 BHK Luxury Apartment');
  const [newPossession, setNewPossession] = useState<'Ready to Move' | 'Dec 2026 Handover' | 'Any'>('Ready to Move');

  // Negative Keywords Drawer/Modal State
  const [manageNegativesOpen, setManageNegativesOpen] = useState(false);
  const [newNegativeTerm, setNewNegativeTerm] = useState('');
  const [newNegativeCategory, setNewNegativeCategory] = useState<NegativeKeyword['category']>('Rental / PG');

  // Filtered Keywords
  const filteredKeywords = keywords.filter((kw) => {
    const matchesSearch = kw.query.toLowerCase().includes(searchFilter.toLowerCase());
    const matchesMarket = selectedMarket === 'all' || kw.microMarket === selectedMarket;
    const matchesPossession = selectedPossession === 'all' || kw.possessionTarget === selectedPossession;
    const matchesIntent = selectedIntent === 'all' || kw.intent === selectedIntent;
    
    let matchesPerformance = true;
    if (selectedPerformance === 'top_performer') {
      matchesPerformance = kw.performanceStatus === 'top_performer' || (kw.avgPosition <= 3.0 && kw.ctr >= 5.0);
    } else if (selectedPerformance === 'rising_star') {
      matchesPerformance = kw.performanceStatus === 'rising_star' || (kw.positionDelta !== undefined && kw.positionDelta >= 1.5);
    } else if (selectedPerformance === 'low_ctr_alert') {
      matchesPerformance = kw.performanceStatus === 'low_ctr_alert' || (kw.impressions >= 10000 && kw.ctr < 2.0);
    } else if (selectedPerformance === 'underperforming') {
      matchesPerformance = kw.performanceStatus === 'underperforming' || kw.avgPosition > 10.0;
    }

    return matchesSearch && matchesMarket && matchesPossession && matchesIntent && matchesPerformance;
  });

  const openExcludeDialog = (kw: KeywordItem) => {
    setTargetKeywordToExclude(kw);
    // Suggest first word stem as default
    const words = kw.query.split(' ').filter((w) => w.length > 2);
    setExclusionStem(words[0] || kw.query);
    setExcludeModalOpen(true);
  };

  const handleConfirmExclusion = () => {
    if (targetKeywordToExclude) {
      onExcludeKeyword(targetKeywordToExclude.id, exclusionReason, exclusionStem, isPermanentExclusion);
      setExcludeModalOpen(false);
      setTargetKeywordToExclude(null);
    }
  };

  const handleCreateKeyword = () => {
    if (!newQuery.trim()) return;
    const item: KeywordItem = {
      id: `kw-${Date.now()}`,
      query: newQuery.trim().toLowerCase(),
      monthlySearches: Math.max(0, Number(newMonthlySearches) || 0),
      impressions: 0,
      clicks: 0,
      ctr: 0,
      avgPosition: 0,
      intent: 'High Commercial',
      microMarket: newMarket,
      typology: newTypology,
      budgetTier: 'Contact for Pricing',
      possessionTarget: newPossession,
      isExcluded: false,
      suggestedAdGroup: `Surat - ${newMarket} ${newTypology}`,
    };
    onAddKeyword(item);
    setAddKeywordModalOpen(false);
    setNewQuery('');
  };

  const handleAddCustomNegative = () => {
    if (!newNegativeTerm.trim()) return;
    const neg: NegativeKeyword = {
      id: `neg-${Date.now()}`,
      term: newNegativeTerm.trim().toLowerCase(),
      category: newNegativeCategory,
      stem: newNegativeTerm.trim().toLowerCase(),
      matchType: 'phrase',
      isPermanent: true,
      addedAt: new Date().toISOString().split('T')[0],
    };
    onAddNegative(neg);
    setNewNegativeTerm('');
  };

  return (
    <Box>
      {/* Header Banner */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: '#faf8f5',
          border: '1px solid #e8dec8',
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <TrendingUpIcon sx={{ color: '#9d6f32', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
                Module 1: Local Keyword Intelligence & Negative Filtering (Surat, Gujarat)
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Import measured Search Console queries or add planning ideas. Manually entered search volume is an estimate; rank and traffic appear only for imported queries.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Exclusion terms are CRM planning notes; they are not sent to Google Ads.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CloudSyncIcon />}
              onClick={() => setGscModalOpen(true)}
              sx={{ fontWeight: 600, textTransform: 'none' }}
            >
              Import Search Console CSV
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={() => setManageNegativesOpen(true)}
              sx={{ fontWeight: 600, textTransform: 'none' }}
            >
              Negative Exclusions ({negativeKeywords.length})
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddKeywordModalOpen(true)}
              sx={{ bgcolor: '#9d6f32', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: '#7a5525' } }}
            >
              + Ingest Search Query
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Filters Bar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e8e8e8', bgcolor: '#fff' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 3.5 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search high-intent buyer queries..."
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Micro-Market</InputLabel>
              <Select value={selectedMarket} label="Micro-Market" onChange={(e) => setSelectedMarket(e.target.value)}>
                <MenuItem value="all">All Micro-Markets</MenuItem>
                <MenuItem value="Vanakla">Vanakla (Lagom & Avsar)</MenuItem>
                <MenuItem value="Jahangirpura">Jahangirpura</MenuItem>
                <MenuItem value="Palanpur Canal">Palanpur Canal Road</MenuItem>
                <MenuItem value="Jahangirabad">Jahangirabad</MenuItem>
                <MenuItem value="Dandi Road">Dandi Road</MenuItem>
                <MenuItem value="Vesu">Vesu</MenuItem>
                <MenuItem value="Pal">Pal / Adajan</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Possession</InputLabel>
              <Select value={selectedPossession} label="Possession" onChange={(e) => setSelectedPossession(e.target.value)}>
                <MenuItem value="all">All Possession Timelines</MenuItem>
                <MenuItem value="Ready to Move">Ready to Move (Swarna Lagom)</MenuItem>
                <MenuItem value="Dec 2026 Handover">Dec 2026 Handover (Swarna Avsar)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.25 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Search Intent</InputLabel>
              <Select value={selectedIntent} label="Search Intent" onChange={(e) => setSelectedIntent(e.target.value)}>
                <MenuItem value="all">All Search Intents</MenuItem>
                <MenuItem value="High Commercial">High Commercial</MenuItem>
                <MenuItem value="Transactional">Transactional</MenuItem>
                <MenuItem value="Navigational">Navigational (Brand)</MenuItem>
                <MenuItem value="Investigational">Investigational</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 6, sm: 2.25 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Query Health / ROI</InputLabel>
              <Select value={selectedPerformance} label="Query Health / ROI" onChange={(e) => setSelectedPerformance(e.target.value)}>
                <MenuItem value="all">All Query Health</MenuItem>
                <MenuItem value="top_performer">🟢 Top Performers (Top 3)</MenuItem>
                <MenuItem value="rising_star">🚀 Rising Stars (+Rank)</MenuItem>
                <MenuItem value="low_ctr_alert">⚠️ Low CTR Alerts (&lt;2%)</MenuItem>
                <MenuItem value="underperforming">🔴 Underperforming / Page 2+</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Keywords Table */}
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e8e8e8' }}>
        <Table size="medium">
          <TableHead sx={{ bgcolor: '#fafafa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>Google Search Query (Surat)</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>Micro-Market & Typology</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>Possession Focus</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13 }}>Estimated volume / impressions</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, fontSize: 13 }}>Average position</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 13 }}>Query Health & Status</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: 13 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredKeywords.map((kw) => {
              const isTop = kw.performanceStatus === 'top_performer' || (kw.avgPosition <= 3.0 && kw.ctr >= 5.0);
              const isRising = kw.performanceStatus === 'rising_star' || (kw.positionDelta !== undefined && kw.positionDelta >= 1.5);
              const isLowCtr = kw.performanceStatus === 'low_ctr_alert' || (kw.impressions >= 10000 && kw.ctr < 2.0);

              return (
                <TableRow
                  key={kw.id}
                  hover
                  sx={{
                    bgcolor: kw.isExcluded ? 'rgba(211, 47, 47, 0.04)' : 'inherit',
                    opacity: kw.isExcluded ? 0.75 : 1,
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography
                        variant="body2"
                        onClick={() => setSelectedKeywordForHistory(kw)}
                        sx={{
                          fontWeight: 700,
                          color: '#1a1a1a',
                          cursor: 'pointer',
                          '&:hover': { color: '#1976d2', textDecoration: 'underline' },
                        }}
                      >
                        {kw.query}
                      </Typography>
                      {kw.isExcluded ? (
                        <Chip
                          label={`Excluded: ${kw.exclusionReason || 'Negative Keyword'}`}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ height: 20, fontSize: 10, mt: 0.5, fontWeight: 600 }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          AdGroup: <strong>{kw.suggestedAdGroup || 'Surat Real Estate'}</strong>
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                      {kw.microMarket}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {kw.typology}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={kw.possessionTarget}
                      size="small"
                      sx={{
                        fontWeight: 700,
                        fontSize: 11,
                        bgcolor: kw.possessionTarget === 'Ready to Move' ? 'rgba(46,125,50,0.12)' : kw.possessionTarget === 'Dec 2026 Handover' ? 'rgba(237,108,2,0.12)' : '#eee',
                        color: kw.possessionTarget === 'Ready to Move' ? '#2e7d32' : kw.possessionTarget === 'Dec 2026 Handover' ? '#ed6c02' : '#666',
                      }}
                    />
                  </TableCell>

                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {kw.monthlySearches ? `${kw.monthlySearches.toLocaleString('en-IN')} est. /mo` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {kw.impressions.toLocaleString('en-IN')} imp
                    </Typography>
                  </TableCell>

                  {/* Rank & 30d Movement */}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 800, color: kw.avgPosition <= 3 ? '#2e7d32' : '#1a1a1a' }}>
                        {kw.avgPosition > 0 ? `#${kw.avgPosition}` : 'No measured rank'}
                      </Typography>
                      {kw.positionDelta !== undefined && (
                        <Chip
                          icon={kw.positionDelta >= 0 ? <TrendingUpIcon sx={{ fontSize: '12px !important' }} /> : <TrendingDownIcon sx={{ fontSize: '12px !important' }} />}
                          label={`${kw.positionDelta >= 0 ? '+' : ''}${kw.positionDelta}`}
                          size="small"
                          color={kw.positionDelta >= 0 ? 'success' : 'error'}
                          sx={{ height: 18, fontSize: 10, fontWeight: 800, px: 0.2 }}
                        />
                      )}
                    </Stack>
                    <Typography variant="caption" sx={{ color: kw.ctr >= 5 ? '#2e7d32' : 'text.secondary' }}>
                      {kw.impressions > 0 ? `${kw.ctr}% CTR (${kw.clicks} clicks)` : 'No measured traffic'}
                    </Typography>
                  </TableCell>

                  {/* Health & Status Badge */}
                  <TableCell>
                    {kw.impressions === 0 ? (
                      <Chip label="Planning idea" size="small" variant="outlined" />
                    ) : isTop ? (
                      <Chip
                        icon={<StarIcon sx={{ fontSize: 13 }} />}
                        label="Top Performer"
                        size="small"
                        color="success"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                    ) : isRising ? (
                      <Chip
                        icon={<RocketLaunchIcon sx={{ fontSize: 13 }} />}
                        label="Rising Star"
                        size="small"
                        color="primary"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                    ) : isLowCtr ? (
                      <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: 13 }} />}
                        label="Low CTR Alert"
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 700, fontSize: 11 }}
                      />
                    ) : (
                      <Chip
                        icon={<TrendingDownIcon sx={{ fontSize: 13 }} />}
                        label="Page 2+"
                        size="small"
                        color="default"
                        sx={{ fontWeight: 600, fontSize: 11 }}
                      />
                    )}
                  </TableCell>

                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title="View measured metrics and available history">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<InsightsIcon />}
                          onClick={() => setSelectedKeywordForHistory(kw)}
                          sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none', borderColor: '#cbd5e1', color: '#334155' }}
                        >
                          Analytics
                        </Button>
                      </Tooltip>

                      {!kw.isExcluded ? (
                        <Tooltip title="Exclude negative / non-buyer query">
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<BlockIcon />}
                            onClick={() => openExcludeDialog(kw)}
                            sx={{ fontSize: 11, fontWeight: 700, textTransform: 'none' }}
                          >
                            Exclude
                          </Button>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="error" sx={{ fontWeight: 700, alignSelf: 'center' }}>
                          Excluded
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Quick Exclude Modal */}
      <Dialog open={excludeModalOpen} onClose={() => setExcludeModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BlockIcon color="error" />
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Negative Keyword Exclusion
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {targetKeywordToExclude && (
            <Stack spacing={2}>
              <Alert severity="warning" sx={{ fontSize: 12 }}>
                Query: <strong>"{targetKeywordToExclude.query}"</strong>
              </Alert>

              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', mb: 1, display: 'block' }}>
                  Click stem to set as negative filter:
                </Typography>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  {targetKeywordToExclude.query.split(' ').map((word, i) => (
                    <Chip
                      key={i}
                      label={word}
                      clickable
                      color={exclusionStem === word ? 'error' : 'default'}
                      onClick={() => setExclusionStem(word)}
                      sx={{ fontWeight: exclusionStem === word ? 700 : 500 }}
                    />
                  ))}
                </Stack>
              </Box>

              <TextField
                label="Exclusion Stem / Phrase"
                size="small"
                fullWidth
                value={exclusionStem}
                onChange={(e) => setExclusionStem(e.target.value)}
              />

              <FormControl fullWidth size="small">
                <InputLabel>Exclusion Reason</InputLabel>
                <Select value={exclusionReason} label="Exclusion Reason" onChange={(e) => setExclusionReason(e.target.value)}>
                  <MenuItem value="Rental / PG query (Not buyer)">Rental / PG query (Not buyer)</MenuItem>
                  <MenuItem value="Used furniture / Non-realty">Used furniture / Non-realty</MenuItem>
                  <MenuItem value="Cheap lodge / room sharing">Cheap lodge / room sharing</MenuItem>
                  <MenuItem value="Legal dispute / Demolition">Legal dispute / Demolition</MenuItem>
                  <MenuItem value="Commercial competitor irrelevant">Commercial competitor irrelevant</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#fafafa' }}>
          <Button onClick={() => setExcludeModalOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleConfirmExclusion} sx={{ fontWeight: 700 }}>
            Add to Negative Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Negative Keywords Management Modal */}
      <Dialog open={manageNegativesOpen} onClose={() => setManageNegativesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <BlockIcon color="error" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Negative Keywords & Stem Exclusions ({negativeKeywords.length})
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          {/* Add New Negative */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2.5, bgcolor: '#fafafa', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#555', display: 'block', mb: 1 }}>
              + Add Custom Negative Filter / Stem
            </Typography>
            <Grid container spacing={1.5} alignItems="center">
              <Grid size={{ xs: 6 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. pg hostel, lease, olx"
                  value={newNegativeTerm}
                  onChange={(e) => setNewNegativeTerm(e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 4 }}>
                <FormControl fullWidth size="small">
                  <Select
                    value={newNegativeCategory}
                    onChange={(e) => setNewNegativeCategory(e.target.value as any)}
                  >
                    <MenuItem value="Rental / PG">Rental / PG</MenuItem>
                    <MenuItem value="Cheap Lodge / Sharing">Cheap Lodge</MenuItem>
                    <MenuItem value="Legal Dispute">Legal Dispute</MenuItem>
                    <MenuItem value="Used Furniture">Used Furniture</MenuItem>
                    <MenuItem value="Custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 2 }}>
                <Button
                  variant="contained"
                  color="error"
                  fullWidth
                  onClick={handleAddCustomNegative}
                  sx={{ height: 40, fontWeight: 700 }}
                >
                  Add
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Existing Negatives List */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {negativeKeywords.map((neg) => (
              <Chip
                key={neg.id}
                label={`[${neg.matchType}] "${neg.term}" · ${neg.category}`}
                color="error"
                variant="outlined"
                onDelete={() => onRemoveNegative(neg.id)}
                sx={{ fontWeight: 600, fontSize: 12, bgcolor: '#fff' }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#fafafa' }}>
          <Button onClick={() => setManageNegativesOpen(false)} sx={{ fontWeight: 700 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Search Query Ingestion Modal */}
      <Dialog open={addKeywordModalOpen} onClose={() => setAddKeywordModalOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Ingest High-Intent Search Query
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={2}>
            <TextField
              label="Search Query (e.g. ready 3 bhk vesu surat)"
              fullWidth
              size="small"
              value={newQuery}
              onChange={(e) => setNewQuery(e.target.value)}
            />
            <TextField
              label="Monthly Search Volume (Est.)"
              type="number"
              fullWidth
              size="small"
              value={newMonthlySearches}
              onChange={(e) => setNewMonthlySearches(Number(e.target.value))}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Micro-Market</InputLabel>
              <Select value={newMarket} label="Micro-Market" onChange={(e) => setNewMarket(e.target.value as any)}>
                <MenuItem value="Vanakla">Vanakla (Swarna Lagom & Avsar)</MenuItem>
                <MenuItem value="Jahangirpura">Jahangirpura</MenuItem>
                <MenuItem value="Palanpur Canal">Palanpur Canal Road</MenuItem>
                <MenuItem value="Jahangirabad">Jahangirabad</MenuItem>
                <MenuItem value="Dandi Road">Dandi Road</MenuItem>
                <MenuItem value="Vesu">Vesu</MenuItem>
                <MenuItem value="Pal">Pal</MenuItem>
                <MenuItem value="Adajan">Adajan</MenuItem>
                <MenuItem value="Althan">Althan</MenuItem>
                <MenuItem value="VIP Road">VIP Road</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Typology</InputLabel>
              <Select value={newTypology} label="Typology" onChange={(e) => setNewTypology(e.target.value as any)}>
                <MenuItem value="2 BHK Flat">2 BHK Flat</MenuItem>
                <MenuItem value="3 BHK Luxury Apartment">3 BHK Luxury Apartment</MenuItem>
                <MenuItem value="4 BHK Penthouse">4 BHK Penthouse</MenuItem>
                <MenuItem value="Commercial Retail Shop">Commercial Retail Shop</MenuItem>
                <MenuItem value="Executive Office Space">Executive Office Space</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Possession Stage</InputLabel>
              <Select value={newPossession} label="Possession Stage" onChange={(e) => setNewPossession(e.target.value as any)}>
                <MenuItem value="Ready to Move">Ready to Move (Swarna Lagom)</MenuItem>
                <MenuItem value="Dec 2026 Handover">Under Construction (Swarna Avsar Dec 2026)</MenuItem>
                <MenuItem value="Any">Any Stage</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#fafafa' }}>
          <Button onClick={() => setAddKeywordModalOpen(false)} sx={{ color: '#666' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateKeyword} sx={{ bgcolor: '#9d6f32', fontWeight: 700 }}>
            Ingest to Intelligence Pool
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Search Console Live Sync Modal */}
      <GoogleSearchConsoleSyncModal
        open={gscModalOpen}
        onClose={() => setGscModalOpen(false)}
        onMergeKeywords={(newKw) => {
          if (onMergeKeywords) {
            onMergeKeywords(newKw);
          } else {
            newKw.forEach((k) => onAddKeyword(k));
          }
        }}
      />

      {/* Time-Series Query Performance & Analytics Modal */}
      <QueryPerformanceHistoryModal
        open={Boolean(selectedKeywordForHistory)}
        keyword={selectedKeywordForHistory}
        onClose={() => setSelectedKeywordForHistory(null)}
        onExclude={(kw) => openExcludeDialog(kw)}
      />
    </Box>
  );
}
